import cookies from './cookies';

import { TChangeEvent } from 'forstok-ui-lib';

import { CONFIG_DATE_RANGE_ANT, CONFIG_DATE_RANGE_ANT7 } from '../mappings/share';

type THeader = {
  authorization: string;
  'Content-Type'?: string;
};

export const setEvScrollDown = (el?: HTMLDivElement | null) => {
  const containerEl = el || document.querySelector<HTMLDivElement>('._refChatMessageContainer');
  if (!containerEl) return;
  const container = containerEl;

  const content = container.firstElementChild as HTMLElement | null;
  if (!content) return;

  let shouldAutoScroll = true;
  let resizeObserver: ResizeObserver | null = null;
  let cleanupTimer: number | null = null;

  const userScrollEvents = ['wheel', 'touchstart', 'pointerdown', 'keydown'];

  function cleanup() {
    resizeObserver?.disconnect();
    resizeObserver = null;
    for (const eventName of userScrollEvents) {
      container.removeEventListener(eventName, stopAutoScroll);
    }
    if (cleanupTimer) {
      window.clearTimeout(cleanupTimer);
      cleanupTimer = null;
    }
  }

  function stopAutoScroll() {
    shouldAutoScroll = false;
    cleanup();
  }

  const scrollToBottom = () => {
    if (!shouldAutoScroll) return;

    if (content.scrollHeight > container.clientHeight) {
      const scrollBehavior = container.style.scrollBehavior;
      container.style.scrollBehavior = 'auto';
      container.scrollTop = content.scrollHeight;
      container.style.scrollBehavior = scrollBehavior;
    }
  };

  for (const eventName of userScrollEvents) {
    container.addEventListener(eventName, stopAutoScroll, { passive: true });
  }

  // scroll immediately (handles cached/text-only content)
  scrollToBottom();

  // re-scroll whenever content height grows (images/videos loading in), unless the user scrolls first.
  resizeObserver = new ResizeObserver(scrollToBottom);
  resizeObserver.observe(content);
  cleanupTimer = window.setTimeout(cleanup, 3000);
};

const resetMessageTextAreaHeight = (textArea: HTMLTextAreaElement): void => {
  const inputSection = textArea.closest<HTMLElement>('._refInput');
  const footer = inputSection?.parentElement;

  textArea.style.height = '';
  textArea.style.overflowY = 'hidden';
  textArea.dataset.baseHeight = '';
  footer?.style.removeProperty('--message-textarea-height-diff');
};

export const syncMessageTextAreaHeight = (textArea: HTMLTextAreaElement): void => {
  if (!textArea.value) {
    resetMessageTextAreaHeight(textArea);
    return;
  }

  const minHeight = Number.parseFloat(window.getComputedStyle(textArea).minHeight);
  const baseHeight = Number.parseFloat(textArea.dataset.baseHeight || '') || textArea.offsetHeight;
  textArea.style.height = '0px';

  const maxHeight = Number.parseFloat(window.getComputedStyle(textArea).maxHeight);
  const nextHeight = Math.min(
    Math.max(baseHeight, textArea.scrollHeight, Number.isNaN(minHeight) ? 0 : minHeight),
    Number.isNaN(maxHeight) ? textArea.scrollHeight : maxHeight,
  );

  textArea.style.height = `${nextHeight}px`;
  textArea.style.overflowY = textArea.scrollHeight > nextHeight ? 'auto' : 'hidden';

  const inputSection = textArea.closest<HTMLElement>('._refInput');
  const footer = inputSection?.parentElement;
  textArea.dataset.baseHeight = `${baseHeight}`;
  if (inputSection) {
    const heightDiff = Math.max(0, nextHeight - baseHeight);
    footer?.style.setProperty('--message-textarea-height-diff', `${heightDiff}px`);
  }
};

export const getConfigDateRange = (dateStorage?: string) => {
  let result = CONFIG_DATE_RANGE_ANT;

  if (dateStorage === 'filterDateComplexRangeValueByReportConversation') {
    result = CONFIG_DATE_RANGE_ANT7;
  }
  return result;
};

export const headers = (type?: string) => {
  const result: THeader = { authorization: cookies.get('token') ? `Bearer ${cookies.get('token')}` : '' };
  type && (result['Content-Type'] = type);
  return result;
};

export const evCountInp: TChangeEvent = (e) => {
  const target = e.target as HTMLInputElement;
  const charLength = target.value.length;
  const valueEl = target.closest('._refInput')?.querySelector('.counter-input') as HTMLSpanElement;
  if (valueEl) valueEl.innerText = charLength.toString();
};

export const generateSUM = (names: string[] | string, data: Record<string, any>[], type?: 'currency'): number => {
  if (!data || !names) return 0;

  const getValue = (obj: Record<string, any>, path: string[] | string): any => {
    if (Array.isArray(path)) {
      return path.reduce(
        (acc: any, key: string): any => (acc && key in acc ? (acc as Record<string, unknown>)[key] : 0),
        obj,
      );
    }
    return (obj[path] ?? 0) as number;
  };

  return data.reduce((sum, item) => {
    let value = getValue(item, names);

    if (type === 'currency' && typeof value === 'string') {
      value = parseFloat(value.replace(/[^0-9.-]+/g, '')) || 0;
    }

    return sum + (typeof value === 'number' ? value : 0);
  }, 0);
};

export const generateOptionValue = <T>(
  value: T | null | undefined,
  label?: string,
): { value: T; label: string } | null => {
  if (value === null || value === undefined) return null;

  return {
    value,
    label: label ?? String(value),
  };
};

const getExtensionType = (extension: string, def?: string): string => {
  switch (extension) {
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'webp':
    case 'bmp':
    case 'svg':
    case 'heic':
    case 'heif':
      return 'image';
    case 'mp4':
    case 'mov':
    case 'avi':
    case 'mkv':
    case 'webm':
    case 'm4v':
      return 'video';
    case 'mp3':
    case 'wav':
    case 'ogg':
    case 'm4a':
    case 'aac':
    case 'flac':
      return 'audio';
    case 'pdf':
      return 'pdf';
    case 'csv':
      return 'csv';
    case 'txt':
      return 'text';
    case 'html':
    case 'htm':
      return 'html';
    case 'xml':
      return 'xml';
    case 'json':
      return 'json';
    case 'xls':
    case 'xlsx':
    case 'ods':
    case 'numbers':
      return 'sheet';
    case 'doc':
    case 'docx':
    case 'odt':
    case 'rtf':
    case 'pages':
      return 'word';
    case 'ppt':
    case 'pptx':
    case 'odp':
    case 'key':
    case 'keynote':
      return 'powerpoint';
    case 'zip':
      return 'zip';
    case 'rar':
      return 'rar';
    case '7z':
      return '7z';
    case 'tar':
      return 'tar';
    case 'gz':
      return 'gzip';
    case 'epub':
      return 'epub';
    default:
      return def ?? '';
  }
};

export const getURLExtension = (url?: string | null): string => {
  if (!url) return '';
  const path = url.split(/[#?]/)[0];
  const extension = path.includes('.') ? path.split('.').pop() || '' : '';

  return getExtensionType(extension);
};

export const getFileNameFromURL = (url?: string | null): string => {
  if (!url) return '';
  try {
    const { pathname } = new URL(url);
    const fileName = pathname.split('/').pop();
    return fileName ? decodeURIComponent(fileName) : '';
  } catch {
    return '';
  }
};

export const getFileType = (file?: File | null): string => {
  if (!file) return '';

  const mimeType = (file.type || '').toLowerCase();
  const fileName = file.name.toLowerCase();
  const extension = fileName.includes('.') ? fileName.split('.').pop() || '' : '';

  if (mimeType.includes('image/')) return 'image';
  if (mimeType.includes('video/')) return 'video';
  if (mimeType.includes('audio/')) return 'audio';
  if (mimeType.includes('application/pdf')) return 'pdf';
  if (mimeType.includes('text/csv')) return 'csv';
  if (mimeType.includes('text/plain')) return 'text';
  if (mimeType.includes('text/html')) return 'html';
  if (mimeType.includes('text/xml') || mimeType.includes('application/xml')) return 'xml';
  if (mimeType.includes('application/json') || mimeType.includes('text/json')) return 'json';
  if (mimeType.includes('application/zip') || mimeType.includes('application/x-zip-compressed')) return 'zip';
  if (mimeType.includes('application/x-rar') || mimeType.includes('application/vnd.rar')) return 'rar';
  if (mimeType.includes('application/x-7z-compressed')) return '7z';
  if (mimeType.includes('application/x-tar')) return 'tar';
  if (mimeType.includes('application/gzip') || mimeType.includes('application/x-gzip')) return 'gzip';

  if (
    mimeType.includes('application/vnd.ms-excel') ||
    mimeType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') ||
    mimeType.includes('application/vnd.oasis.opendocument.spreadsheet')
  ) {
    return 'sheet';
  }

  if (
    mimeType.includes('application/msword') ||
    mimeType.includes('application/vnd.openxmlformats-officedocument.wordprocessingml.document') ||
    mimeType.includes('application/vnd.oasis.opendocument.text') ||
    mimeType.includes('application/rtf')
  ) {
    return 'word';
  }

  if (
    mimeType.includes('application/vnd.ms-powerpoint') ||
    mimeType.includes('application/vnd.openxmlformats-officedocument.presentationml.presentation') ||
    mimeType.includes('application/vnd.oasis.opendocument.presentation')
  ) {
    return 'powerpoint';
  }

  if (mimeType.includes('application/epub+zip')) return 'epub';

  return getExtensionType(extension, mimeType.includes('application/') ? 'document' : '');
};

export const forceDownload = (fileUrl: string) => {
  if (!fileUrl) return null;

  console.log(fileUrl, 'fileUrl');
  fetch(fileUrl)
    .then((response) => response.blob())
    .then((blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = getFileNameFromURL(fileUrl);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    })
    .catch(() => alert('Download failed. Server blocks access.'));
};
